import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import {Axios} from './../../services/axios';
import {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import moment from 'moment';
import {Block, Loading} from 'notiflix';
import MDButton from 'components/MDButton';
import GeneralModal from 'components/MDModal/Modal';
import {useForm} from 'react-hook-form';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  MoreVert,
  ContentCopy,
  Delete,
  Visibility,
  Edit,
  Layers,
  History,
  LibraryAdd,
  Search,
} from '@mui/icons-material';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Page Name', accessor: 'name', width: '20%', align: 'left'},
  {Header: 'Route', accessor: 'route', width: '15%', align: 'left'},
  {Header: 'Type', accessor: 'pageType', align: 'center', width: '8%'},
  {
    Header: 'Dynamic Config',
    accessor: 'dynamicConfig',
    align: 'center',
    width: '12%',
  },
  {Header: 'Sections', accessor: 'sections', align: 'center', width: '8%'},
  {Header: 'Created', accessor: 'createdOn', align: 'center', width: '12%'},
  {Header: 'Updated', accessor: 'updatedOn', align: 'center', width: '12%'},
  {Header: 'Status', accessor: 'status', align: 'center', width: '8%'},
  {Header: 'Actions', accessor: 'action', align: 'center', width: '10%'},
];

// Predefined dynamic entity configurations
const DYNAMIC_ENTITY_CONFIGS = [
  {
    id: 'product',
    name: 'Product',
    route: '/api/products',
    description: 'For product detail pages',
  },
  {
    id: 'valuepack',
    name: 'ValuePack',
    route: '/api/valuepacks',
    description: 'For valuepack detail pages',
  },
  {
    id: 'category',
    name: 'Category',
    route: '/api/categories',
    description: 'For category detail pages',
  },
];

const PAGE_SIZE = 10;

const getRow = (row, idx, handleMenuOpen, canUpdate, canDelete) => ({
  sr: (
    <MDTypography variant="caption" fontWeight="medium">
      {idx + 1}
    </MDTypography>
  ),
  name: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {row.name}
    </MDTypography>
  ),
  route: (
    <MDTypography variant="caption" color="text" fontFamily="monospace">
      {row.route}
    </MDTypography>
  ),
  pageType: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={row.pageType}
        color={row.pageType === 'STATIC' ? 'info' : 'warning'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  dynamicConfig: (
    <MDBox ml={-1}>
      {row.dynamicEntities && row.dynamicEntities.length > 0 ? (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
          {row.dynamicEntities.map((entity, index) => (
            <Chip
              key={index}
              label={entity.name}
              color="primary"
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      ) : (
        <MDBadge
          badgeContent="None"
          color="secondary"
          variant="gradient"
          size="sm"
        />
      )}
    </MDBox>
  ),
  sections: (
    <MDBox ml={-1}>
      <Chip
        label={row.sectionCount || 0}
        color={row.sectionCount > 0 ? 'success' : 'default'}
        size="small"
      />
    </MDBox>
  ),
  createdOn: (
    <MDTypography variant="caption">
      {moment(row.createdAt).format('DD MMM YYYY')}
    </MDTypography>
  ),
  updatedOn: (
    <MDTypography variant="caption">
      {moment(row.updatedAt).format('DD MMM YYYY')}
    </MDTypography>
  ),
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={row.active ? 'active' : 'inactive'}
        color={row.active ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  action: (
    <MDBox display="flex" gap={1} ml={-1}>
      {canUpdate && (
        <Tooltip title="Edit Page">
          <IconButton
            size="small"
            color="primary"
            component={Link}
            to={`/pages/edit/${row.id}`}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {(canUpdate || canDelete) && (
        <Tooltip title="More Actions">
          <IconButton size="small" onClick={e => handleMenuOpen(e, row, 'menu')}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </MDBox>
  ),
});

function PagesList() {
  const navigate = useNavigate();
  const canRead = usePermission('page-management', 'read');
  const canCreate = usePermission('page-management', 'create');
  const canUpdate = usePermission('page-management', 'update');
  const canDelete = usePermission('page-management', 'delete');
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    hasMore: false,
  });

  const {
    register,
    handleSubmit,
    formState: {errors},
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      route: '',
      active: true,
      pageType: 'STATIC',
      image: null,
      image_url: '',
      dynamicEntities: [],
      layoutTemplate: 'default',
      pageSettings: {},
    },
  });

  const fetchPages = async () => {
    if (!canRead) {
      setIsLoading(false);
      return;
    }
    try {
      Block.circle('.container', 'Loading Pages...');
      setIsLoading(true);

      const res = await Axios.fetch('page/list', {
        params: {
          search: searchTerm,
          page: currentPage,
          limit: PAGE_SIZE,
        },
      });

      const data = res?.data;
      setPages(data?.data?.pages || []);
      setPagination({
        total: data?.data?.pagination?.total || 0,
        totalPages: data?.data?.pagination?.totalPages || 1,
        hasMore: data?.data?.pagination?.hasMore || false,
      });
    } catch (err) {
      console.error('API Error:', err);
    } finally {
      setIsLoading(false);
      Block.remove('.container');
    }
  };

  useEffect(() => {
    if (!canRead) {
      setIsLoading(false);
      return;
    }
    fetchPages();
  }, [searchTerm, currentPage, canRead]);

  useEffect(() => {
    if (isLoading) {
      Block.circle('.container', 'Loading Pages...');
    } else {
      Block.remove('.container');
    }
  }, [isLoading]);

  const handleAddNewClick = () => {
    reset({
      name: '',
      route: '',
      active: true,
      pageType: 'STATIC',
      image: null,
      image_url: '',
      dynamicEntities: [],
      layoutTemplate: 'default',
      pageSettings: {},
    });
    setOpen(true);
  };

  const handleMenuOpen = (event, page, type) => {
    setAnchorEl(event.currentTarget);
    setSelectedPage(page);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPage(null);
  };

  const handleModalClose = () => {
    setOpen(false);
    reset();
  };

  const handleDynamicEntityChange = event => {
    const selectedIds = event.target.value;
    const selectedEntities = DYNAMIC_ENTITY_CONFIGS.filter(entity =>
      selectedIds.includes(entity.id),
    );
    setValue('dynamicEntities', selectedEntities);
  };

  const onSubmitPageData = async data => {
    try {
      Block.circle('.page-settings-form', 'Creating new page...');
      Loading.circle('Creating new Page...');

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('route', data.route);
      formData.append('active', data.active.toString());

      const detectedPageType = data.route.includes(':') ? 'DYNAMIC' : 'STATIC';
      formData.append('pageType', detectedPageType);

      if (
        detectedPageType === 'DYNAMIC' &&
        data.dynamicEntities &&
        data.dynamicEntities.length > 0
      ) {
        formData.append(
          'dynamicEntities',
          JSON.stringify(data.dynamicEntities),
        );
      }

      formData.append('layoutTemplate', data.layoutTemplate || 'default');
      formData.append('pageSettings', JSON.stringify(data.pageSettings || {}));

      if (data.image && data.image instanceof File) {
        formData.append('image', data.image);
      }

      await Axios.post('page/addNewPage', formData, {
        'Content-Type': 'multipart/form-data',
      });

      Block.remove('.page-settings-form');
      Loading.remove();
      handleModalClose();
      setCurrentPage(1);
      fetchPages();

      alert('Page created successfully!');
    } catch (err) {
      console.error('Upload Error:', err);
      Block.remove('.page-settings-form');
      Loading.remove();
      alert(err.response?.data?.message || 'Error creating page');
    }
  };

  const handleDelete = async () => {
    try {
      Block.circle('#pages-list');
      await Axios.delete(`/page/${selectedPage.id}?label=${encodeURIComponent(selectedPage.name)}`);
      // If we deleted the last item on this page, go back one page
      if (pages.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        fetchPages();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.message || 'Error deleting page');
    } finally {
      Block.remove('#pages-list');
      handleMenuClose();
    }
  };

  const handleDuplicate = async () => {
    try {
      Block.circle('#pages-list');

      const newName = prompt(
        'Enter new page name:',
        `${selectedPage.name} (Copy)`,
      );

      if (newName) {
        const baseRoute = selectedPage.route;
        const newRoute = `${baseRoute}-copy-${Date.now()}`;

        await Axios.post(`pages/${selectedPage.id}/duplicate`, {
          newName,
          newRoute,
          includeSections: true,
        });

        fetchPages();
      }
    } catch (error) {
      console.error('Duplicate error:', error);
      alert(error.response?.data?.message || 'Error duplicating page');
    } finally {
      Block.remove('#pages-list');
      handleMenuClose();
    }
  };

  // Serial number globally correct across pages
  const tableRows = useMemo(() => {
    if (!pages || !Array.isArray(pages)) return [];
    const offset = (currentPage - 1) * PAGE_SIZE;
    return pages.map((row, idx) => getRow(row, idx + offset, handleMenuOpen, canUpdate, canDelete));
  }, [pages, currentPage, canUpdate, canDelete]);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const {totalPages} = pagination;
    if (totalPages <= 5) {
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const pages = [];
    pages.push(1);

    if (currentPage > 3) {
      pages.push('...');
    }

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('...');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to pages. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <>
      <GeneralModal
        open={open}
        onClose={handleModalClose}
        onSubmitPageData={onSubmitPageData}
        register={register}
        watch={watch}
        setValue={setValue}
        handleSubmit={handleSubmit}
        errors={errors}
        isEditing={false}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedPage)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <MenuItem
          onClick={() => {
            navigate(`/pages/${selectedPage?.id}/sections`);
            handleMenuClose();
          }}>
          <Layers fontSize="small" sx={{mr: 1}} /> Manage Sections
        </MenuItem>
        <MenuItem
          disabled
          onClick={() => {
            navigate(`/pages/${selectedPage?.id}/versions`);
            handleMenuClose();
          }}>
          <History fontSize="small" sx={{mr: 1}} /> Version History
        </MenuItem>
        {canDelete && (
          <MenuItem onClick={handleDelete} sx={{color: 'error.main'}}>
            <Delete fontSize="small" sx={{mr: 1}} /> Delete
          </MenuItem>
        )}
      </Menu>

      <div className="container">
        <DashboardLayout>
          <DashboardNavbar />
          <MDBox pt={6} pb={3}>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info">
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <MDTypography variant="h6" color="white">
                          Pages Management
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search pages..."
                          value={searchTerm}
                          onChange={e => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          sx={{bgcolor: 'white', borderRadius: 1}}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4} sx={{textAlign: 'right'}}>
                        {canCreate && (
                          <MDButton
                            variant="gradient"
                            color="dark"
                            onClick={handleAddNewClick}>
                            + Add New Page
                          </MDButton>
                        )}
                      </Grid>
                    </Grid>
                  </MDBox>

                  <MDBox pt={3}>
                    {/* Table inside overflow container for horizontal scroll */}
                    <MDBox sx={{overflowX: 'auto'}}>
                      <DataTable
                        table={{
                          columns,
                          rows: tableRows,
                        }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                        hoverEffect
                      />
                    </MDBox>

                    {/* Server-side pagination — OUTSIDE the overflow container */}
                    {pagination.total > 0 && (
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        px={3}
                        py={2}
                        sx={{borderTop: '1px solid #e0e0e0'}}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{mb: {xs: 1, md: 0}}}>
                          Showing{' '}
                          {Math.min(
                            (currentPage - 1) * PAGE_SIZE + 1,
                            pagination.total,
                          )}
                          –{Math.min(currentPage * PAGE_SIZE, pagination.total)}{' '}
                          of {pagination.total} pages
                        </MDTypography>

                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}>
                            Previous
                          </MDButton>

                          {getPageNumbers().map((item, idx) =>
                            item === '...' ? (
                              <MDTypography
                                key={`dots-${idx}`}
                                variant="caption"
                                sx={{px: 0.5, userSelect: 'none'}}>
                                …
                              </MDTypography>
                            ) : (
                              <MDButton
                                key={item}
                                variant={
                                  item === currentPage ? 'gradient' : 'outlined'
                                }
                                color="info"
                                size="small"
                                sx={{minWidth: 36}}
                                onClick={() => setCurrentPage(item)}>
                                {item}
                              </MDButton>
                            ),
                          )}

                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            disabled={currentPage >= pagination.totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}>
                            Next
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
          <Footer />
        </DashboardLayout>
      </div>
    </>
  );
}

export default PagesList;

// import Grid from '@mui/material/Grid';
// import Card from '@mui/material/Card';
// import MDBox from 'components/MDBox';
// import MDTypography from 'components/MDTypography';
// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// import Footer from 'examples/Footer';
// import DataTable from 'examples/Tables/DataTable';
// import MDBadge from 'components/MDBadge';
// import {Axios} from './../../services/axios';
// import {useEffect, useMemo, useState} from 'react';
// import {Link, useNavigate} from 'react-router-dom';
// import moment from 'moment';
// import {Block, Loading} from 'notiflix';
// import MDButton from 'components/MDButton';
// import GeneralModal from 'components/MDModal/Modal';
// import {useForm} from 'react-hook-form';
// import {
//   Box,
//   Chip,
//   IconButton,
//   Tooltip,
//   Menu,
//   MenuItem,
//   TextField,
//   InputAdornment,
//   FormControlLabel,
//   Switch,
//   Alert,
// } from '@mui/material';
// import {
//   MoreVert,
//   ContentCopy,
//   Delete,
//   Visibility,
//   Edit,
//   Layers,
//   History,
//   LibraryAdd,
//   Search,
// } from '@mui/icons-material';

// const columns = [
//   {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
//   {Header: 'Page Name', accessor: 'name', width: '20%', align: 'left'},
//   {Header: 'Route', accessor: 'route', width: '15%', align: 'left'},
//   {Header: 'Type', accessor: 'pageType', align: 'center', width: '8%'},
//   {
//     Header: 'Dynamic Config',
//     accessor: 'dynamicConfig',
//     align: 'center',
//     width: '12%',
//   },
//   {Header: 'Sections', accessor: 'sections', align: 'center', width: '8%'},
//   {Header: 'Created', accessor: 'createdOn', align: 'center', width: '12%'},
//   {Header: 'Updated', accessor: 'updatedOn', align: 'center', width: '12%'},
//   {Header: 'Status', accessor: 'status', align: 'center', width: '8%'},
//   {Header: 'Actions', accessor: 'action', align: 'center', width: '10%'},
// ];

// // Predefined dynamic entity configurations
// const DYNAMIC_ENTITY_CONFIGS = [
//   {
//     id: 'product',
//     name: 'Product',
//     route: '/api/products',
//     description: 'For product detail pages',
//   },
//   {
//     id: 'valuepack',
//     name: 'ValuePack',
//     route: '/api/valuepacks',
//     description: 'For valuepack detail pages',
//   },
//   {
//     id: 'category',
//     name: 'Category',
//     route: '/api/categories',
//     description: 'For category detail pages',
//   },
// ];

// const getRow = (row, idx, handleMenuOpen) => ({
//   sr: (
//     <MDTypography variant="caption" fontWeight="medium">
//       {idx + 1}
//     </MDTypography>
//   ),
//   name: (
//     <MDTypography display="block" variant="button" fontWeight="medium">
//       {row.name}
//     </MDTypography>
//   ),
//   route: (
//     <MDTypography variant="caption" color="text" fontFamily="monospace">
//       {row.route}
//     </MDTypography>
//   ),
//   pageType: (
//     <MDBox ml={-1}>
//       <MDBadge
//         badgeContent={row.pageType}
//         color={row.pageType === 'STATIC' ? 'info' : 'warning'}
//         variant="gradient"
//         size="sm"
//       />
//     </MDBox>
//   ),
//   dynamicConfig: (
//     <MDBox ml={-1}>
//       {row.dynamicEntities && row.dynamicEntities.length > 0 ? (
//         <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
//           {row.dynamicEntities.map((entity, index) => (
//             <Chip
//               key={index}
//               label={entity.name}
//               color="primary"
//               size="small"
//               variant="outlined"
//             />
//           ))}
//         </Box>
//       ) : (
//         <MDBadge
//           badgeContent="None"
//           color="secondary"
//           variant="gradient"
//           size="sm"
//         />
//       )}
//     </MDBox>
//   ),
//   sections: (
//     <MDBox ml={-1}>
//       <Chip
//         label={row.sectionCount || 0}
//         color={row.sectionCount > 0 ? 'success' : 'default'}
//         size="small"
//       />
//     </MDBox>
//   ),
//   createdOn: (
//     <MDTypography variant="caption">
//       {moment(row.createdAt).format('DD MMM YYYY')}
//     </MDTypography>
//   ),
//   updatedOn: (
//     <MDTypography variant="caption">
//       {moment(row.updatedAt).format('DD MMM YYYY')}
//     </MDTypography>
//   ),
//   status: (
//     <MDBox ml={-1}>
//       <MDBadge
//         badgeContent={row.active ? 'active' : 'inactive'}
//         color={row.active ? 'success' : 'secondary'}
//         variant="gradient"
//         size="sm"
//       />
//     </MDBox>
//   ),
//   action: (
//     <MDBox display="flex" gap={1} ml={-1}>
//       <Tooltip title="Edit Page">
//         <IconButton
//           size="small"
//           color="primary"
//           component={Link}
//           to={`/pages/edit/${row.id}`}>
//           <Edit fontSize="small" />
//         </IconButton>
//       </Tooltip>
//       <Tooltip title="More Actions">
//         <IconButton size="small" onClick={e => handleMenuOpen(e, row, 'menu')}>
//           <MoreVert fontSize="small" />
//         </IconButton>
//       </Tooltip>
//     </MDBox>
//   ),
// });

// function PagesList() {
//   const navigate = useNavigate();
//   const [pages, setPages] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [open, setOpen] = useState(false);
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [selectedPage, setSelectedPage] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');

//   const {
//     register,
//     handleSubmit,
//     formState: {errors},
//     watch,
//     setValue,
//     reset,
//   } = useForm({
//     defaultValues: {
//       name: '',
//       route: '',
//       active: true,
//       pageType: 'STATIC',
//       image: null,
//       image_url: '',
//       dynamicEntities: [],
//       layoutTemplate: 'default',
//       pageSettings: {},
//     },
//   });

//   const fetchPages = async () => {
//     try {
//       setIsLoading(true);
//       const res = await Axios.fetch('page/list', {
//         params: {search: searchTerm},
//       });

//       const data = res?.data;
//       setPages(data?.data?.pages || []);
//     } catch (err) {
//       console.error('API Error:', err);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchPages();
//   }, [searchTerm]);

//   useEffect(() => {
//     if (isLoading) {
//       Block.circle('.container', 'Loading Pages...');
//     } else {
//       Block.remove('.container');
//     }
//   }, [isLoading]);

//   const handleAddNewClick = () => {
//     reset({
//       name: '',
//       route: '',
//       active: true,
//       pageType: 'STATIC',
//       image: null,
//       image_url: '',
//       dynamicEntities: [],
//       layoutTemplate: 'default',
//       pageSettings: {},
//     });
//     setOpen(true);
//   };

//   const handleMenuOpen = (event, page, type) => {
//     setAnchorEl(event.currentTarget);
//     setSelectedPage(page);
//   };

//   const handleMenuClose = () => {
//     setAnchorEl(null);
//     setSelectedPage(null);
//   };

//   const handleModalClose = () => {
//     setOpen(false);
//     reset();
//   };

//   const handleDynamicEntityChange = event => {
//     const selectedIds = event.target.value;
//     const selectedEntities = DYNAMIC_ENTITY_CONFIGS.filter(entity =>
//       selectedIds.includes(entity.id),
//     );
//     setValue('dynamicEntities', selectedEntities);
//   };

//   const onSubmitPageData = async data => {
//     try {
//       Block.circle('.page-settings-form', 'Creating new page...');
//       Loading.circle('Creating new Page...');

//       const formData = new FormData();
//       formData.append('name', data.name);
//       formData.append('route', data.route);
//       formData.append('active', data.active.toString());

//       const detectedPageType = data.route.includes(':') ? 'DYNAMIC' : 'STATIC';
//       formData.append('pageType', detectedPageType);

//       if (
//         detectedPageType === 'DYNAMIC' &&
//         data.dynamicEntities &&
//         data.dynamicEntities.length > 0
//       ) {
//         formData.append(
//           'dynamicEntities',
//           JSON.stringify(data.dynamicEntities),
//         );
//       }

//       formData.append('layoutTemplate', data.layoutTemplate || 'default');
//       formData.append('pageSettings', JSON.stringify(data.pageSettings || {}));

//       if (data.image && data.image instanceof File) {
//         formData.append('image', data.image);
//       }

//       await Axios.post('page/addNewPage', formData, {
//         'Content-Type': 'multipart/form-data',
//       });

//       Block.remove('.page-settings-form');
//       Loading.remove();
//       handleModalClose();
//       fetchPages();

//       alert('Page created successfully!');
//     } catch (err) {
//       console.error('Upload Error:', err);
//       Block.remove('.page-settings-form');
//       Loading.remove();
//       alert(err.response?.data?.message || 'Error creating page');
//     }
//   };

//   const handleDelete = async () => {
//     try {
//       Block.circle('#pages-list');
//       await Axios.delete(`/page/${selectedPage.id}`);
//       fetchPages();
//     } catch (error) {
//       console.error('Delete error:', error);
//       alert(error.response?.data?.message || 'Error deleting page');
//     } finally {
//       Block.remove('#pages-list');
//       handleMenuClose();
//     }
//   };

//   const handleDuplicate = async () => {
//     try {
//       Block.circle('#pages-list');

//       const newName = prompt(
//         'Enter new page name:',
//         `${selectedPage.name} (Copy)`,
//       );

//       if (newName) {
//         const baseRoute = selectedPage.route;
//         const newRoute = `${baseRoute}-copy-${Date.now()}`;

//         await Axios.post(`pages/${selectedPage.id}/duplicate`, {
//           newName,
//           newRoute,
//           includeSections: true,
//         });

//         fetchPages();
//       }
//     } catch (error) {
//       console.error('Duplicate error:', error);
//       alert(error.response?.data?.message || 'Error duplicating page');
//     } finally {
//       Block.remove('#pages-list');
//       handleMenuClose();
//     }
//   };

//   const tableRows = useMemo(() => {
//     if (!pages || !Array.isArray(pages)) return [];
//     return pages.map((row, idx) => getRow(row, idx, handleMenuOpen));
//   }, [pages]);

//   return (
//     <>
//       <GeneralModal
//         open={open}
//         onClose={handleModalClose}
//         onSubmitPageData={onSubmitPageData}
//         register={register}
//         watch={watch}
//         setValue={setValue}
//         handleSubmit={handleSubmit}
//         errors={errors}
//         isEditing={false}
//       />

//       <Menu
//         anchorEl={anchorEl}
//         open={Boolean(anchorEl) && Boolean(selectedPage)}
//         onClose={handleMenuClose}
//         anchorOrigin={{
//           vertical: 'bottom',
//           horizontal: 'right',
//         }}
//         transformOrigin={{
//           vertical: 'top',
//           horizontal: 'right',
//         }}>
//         <MenuItem
//           onClick={() => {
//             navigate(`/pages/${selectedPage?.id}/sections`);
//             handleMenuClose();
//           }}>
//           <Layers fontSize="small" sx={{mr: 1}} /> Manage Sections
//         </MenuItem>
//         <MenuItem
//           disabled
//           onClick={() => {
//             navigate(`/pages/${selectedPage?.id}/versions`);
//             handleMenuClose();
//           }}>
//           <History fontSize="small" sx={{mr: 1}} /> Version History
//         </MenuItem>
//         <MenuItem onClick={handleDelete} sx={{color: 'error.main'}}>
//           <Delete fontSize="small" sx={{mr: 1}} /> Delete
//         </MenuItem>
//       </Menu>

//       <div className="container">
//         <DashboardLayout>
//           <DashboardNavbar />
//           <MDBox pt={6} pb={3}>
//             <Grid container spacing={6}>
//               <Grid item xs={12}>
//                 <Card>
//                   <MDBox
//                     mx={2}
//                     mt={-3}
//                     py={3}
//                     px={2}
//                     variant="gradient"
//                     bgColor="info"
//                     borderRadius="lg"
//                     coloredShadow="info">
//                     <Grid container spacing={2} alignItems="center">
//                       <Grid item xs={12} md={4}>
//                         <MDTypography variant="h6" color="white">
//                           Pages Management
//                         </MDTypography>
//                       </Grid>
//                       <Grid item xs={12} md={4}>
//                         <TextField
//                           fullWidth
//                           size="small"
//                           placeholder="Search pages..."
//                           value={searchTerm}
//                           onChange={e => setSearchTerm(e.target.value)}
//                           sx={{bgcolor: 'white', borderRadius: 1}}
//                           InputProps={{
//                             startAdornment: (
//                               <InputAdornment position="start">
//                                 <Search />
//                               </InputAdornment>
//                             ),
//                           }}
//                         />
//                       </Grid>
//                       <Grid item xs={12} md={4} sx={{textAlign: 'right'}}>
//                         <MDButton
//                           variant="gradient"
//                           color="dark"
//                           onClick={handleAddNewClick}>
//                           + Add New Page
//                         </MDButton>
//                       </Grid>
//                     </Grid>
//                   </MDBox>
//                   <MDBox pt={3}>
//                     {/* <DataTable
//                       table={{
//                         columns,
//                         rows:
//                           pages && Array.isArray(pages)
//                             ? pages.map((row, idx) =>
//                                 getRow(row, idx, handleMenuOpen),
//                               )
//                             : [],
//                       }}
//                       isSorted={false}
//                       entriesPerPage={false}
//                       showTotalEntries={false}
//                       noEndBorder
//                       hoverEffect
//                       onPageChange={handleMenuClose}
//                     /> */}

//                     <DataTable
//                       table={{
//                         columns,
//                         rows: tableRows,
//                       }}
//                       isSorted={false}
//                       entriesPerPage={{
//                         defaultValue: 10,
//                         entries: [5, 10, 15, 20, 25],
//                       }}
//                       showTotalEntries={true}
//                       noEndBorder
//                       hoverEffect
//                     />
//                   </MDBox>
//                 </Card>
//               </Grid>
//             </Grid>
//           </MDBox>
//           <Footer />
//         </DashboardLayout>
//       </div>
//     </>
//   );
// }

// export default PagesList;
